/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

import type { DemandSource, DemandSourceMethod } from '../types';
import { extractProtocolVersion } from '../components/shared/editors/parameter-editor.adapters';

/**
 * The source's protocol version, else the per-type default.
 *
 * Shared because it decides which catalog validates a source, so the detail page's
 * editors and the readiness panel's issue list must answer it identically — a panel
 * validating 2.5 against an editor validating 2.6 would report issues the section
 * below it does not have. The reader itself lives in the params adapter.
 */
export function protocolVersionOf(source: Pick<DemandSource, 'protocolVersion' | 'method'>): string {
  return extractProtocolVersion(source, source.method === 'ORTB' ? '2.6' : '4.0');
}

/**
 * Detect the protocol from an endpoint URL.
 *
 * A **suggestion**, not a decision — the Connection step says so ("Detected from the
 * endpoint. Change it if the detection is wrong."), and the operator can override it.
 * That asymmetry is why the bid-path pattern below is deliberately generous: a false
 * positive is one click to correct, while a false negative silently starts the operator
 * on the wrong protocol, and every subsequent step — body editor, catalog, validation —
 * then describes a source they are not building.
 */
export function detectTypeFromUrl(url: string): DemandSourceMethod | null {
  if (!url) return null;
  const lower = url.toLowerCase();
  // OpenRTB signals: explicit path segments or query params.
  //
  // `\/bid[a-z]*\b` rather than `\/bid(der)?\b`: the word boundary after `bid`
  // required the segment to END there, so real endpoints missed. Springserve's
  // `https://tv.springserve.com/bidr/1009` is ORTB and was detected as nothing —
  // reported from user testing. `bidr`, `bidrequest` and `bids` are all live shapes.
  // `openrtb` is a substring test, so Prebid Server's `/openrtb2/auction` matches.
  //
  // `\/o?rtb[a-z0-9]*\b` rather than `\/rtb\b`, for the same reason and found the same way
  // (Todd, 2026-08-20, validating #1120): **`/ortb` did not match anything at all.** FreeWheel's
  // real SSP endpoint is `https://{networkId}.v.fwmrm.net/ortb/ssp` — `/ortb` contains `ortb`, not
  // `/rtb`, so every ORTB signal missed it and every VAST signal missed it too, and the type came
  // from the FreeWheel preset's `method: 'VAST'` instead (#1116). Our own proxy serves `/ortb`,
  // which is how obvious this is in hindsight.
  //
  // A false negative here is worse than the one that produced the `bid[a-z]*` widening, because it
  // does not leave the operator with *nothing*: a preset match then supplies a confident wrong
  // answer, and the suggestion the comment above calls correctable is one the operator has no
  // reason to doubt.
  if (
    /openrtb/.test(lower) ||
    /\/bid[a-z]*\b/.test(lower) ||
    /\/o?rtb[a-z0-9]*\b/.test(lower) ||
    /[?&]openrtb/.test(lower)
  ) {
    return 'ORTB';
  }
  // VAST signals
  if (
    /\/vast\b/.test(lower) ||
    /[?&]vast/.test(lower) ||
    /vast\.xml/.test(lower) ||
    /adtag/.test(lower) ||
    /adserver/.test(lower) ||
    /\bvast\b/.test(lower)
  ) {
    return 'VAST';
  }
  return null;
}
